import { useRoute } from 'vue-router';

export function usePaginationMetadata() {
  const route = useRoute();
  const { public: { domain } } = useRuntimeConfig();

  // Check if current page has pagination
  const hasPagination = computed(() =>
    Object.keys(route.query).some(key => key.startsWith('page_')),
  );

  // Get current page number
  const currentPage = computed(() => {
    const pageParam = Object.keys(route.query).find(key => key.startsWith('page_'));
    if (!pageParam) { return 1; }

    const pageNum = parseInt(route.query[pageParam] as string, 10);
    return !isNaN(pageNum) && pageNum >= 1 ? pageNum : 1;
  });

  // Get the page parameter name (e.g., 'page_abc123')
  const pageParam = computed(() =>
    Object.keys(route.query).find(key => key.startsWith('page_')),
  );

  // Helper function to add pagination parameters to URL
  const addPaginationToUrl = (url: string, query?: Record<string, any>) => {
    if (!hasPagination.value && !query) { return url; }

    const urlObj = new URL(url);
    const queryToUse = query || route.query;

    Object.entries(queryToUse).forEach(([key, value]) => {
      if (value) { urlObj.searchParams.set(key, value as string); }
    });
    return urlObj.toString();
  };

  // Generate pagination metadata links
  const generatePaginationLinks = (totalPosts: number, postsPerPage: number, listingKey?: string) => {
    const totalPages = Math.ceil(totalPosts / postsPerPage);
    if (totalPages <= 1) { return []; }

    // Get the page parameter name (e.g., 'page_listing_abc123')
    const currentPageParam = pageParam.value;

    // Determine the page parameter name
    let pageParamName: string | null = null;

    if (currentPageParam) {
      // If there's already a page parameter in the URL, use it
      pageParamName = currentPageParam;
    } else if (listingKey) {
      // If we're on page 1 and have a listingKey, construct the parameter name
      pageParamName = `page_${listingKey}`;
    }

    if (!pageParamName) { return []; }

    const links = [];

    // Add prev link if not on first page
    if (currentPage.value > 1) {
      const prevQuery = { ...route.query };
      const prevPage = currentPage.value - 1;

      if (prevPage === 1) {
        delete prevQuery[pageParamName];
      } else {
        prevQuery[pageParamName] = prevPage.toString();
      }

      const prevUrl = addPaginationToUrl(`${domain}${route.path}`, prevQuery);
      links.push({ rel: 'prev', href: prevUrl });
    }

    // Add next link if not on last page
    if (currentPage.value < totalPages) {
      const nextQuery = { ...route.query };
      const nextPage = currentPage.value + 1;
      nextQuery[pageParamName] = nextPage.toString();

      const nextUrl = addPaginationToUrl(`${domain}${route.path}`, nextQuery);
      links.push({ rel: 'next', href: nextUrl });
    }

    return links;
  };

  // Generate pagination links for UI components
  const generatePaginationLinksForUI = (totalPosts: number, postsPerPage: number, listingKey: string) => {
    const totalPages = Math.ceil(totalPosts / postsPerPage);
    const links = [];

    for (let i = 1; i <= totalPages; i++) {
      const query = { ...route.query };
      const pageParamName = `page_${listingKey}`;

      if (i === 1) {
        delete query[pageParamName];
      } else {
        query[pageParamName] = i.toString();
      }

      links.push({
        page: i,
        href: { path: route.path, query },
        isCurrent: i === currentPage.value,
      });
    }

    return links;
  };

  // Handle pagination navigation
  const handlePagination = (selectedPage: number, listingKey: string, blockInstance?: HTMLElement | null) => {
    const router = useRouter();
    const currentQuery = { ...route.query };
    const pageParamName = `page_${listingKey}`;

    if (selectedPage === 1) {
      delete currentQuery[pageParamName];
    } else {
      currentQuery[pageParamName] = selectedPage.toString();
    }

    router.push({ query: currentQuery });

    if (blockInstance) {
      blockInstance.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate pagination SEO metadata
  const generatePaginationSeoMeta = (baseTitle: string) => {
    if (!hasPagination.value || currentPage.value <= 1) { return null; }

    const url = addPaginationToUrl(`${domain}${route.path}`);
    const paginatedTitle = `${baseTitle} - Page ${currentPage.value}`;

    return {
      canonicalUrl: url,
      title: paginatedTitle,
      ogTitle: paginatedTitle,
      ogUrl: url,
    };
  };

  return {
    hasPagination,
    currentPage,
    pageParam,
    addPaginationToUrl,
    generatePaginationLinks,
    generatePaginationLinksForUI,
    handlePagination,
    generatePaginationSeoMeta,
  };
}
